import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { message } from "antd";
import ChatService from "../../../Services/ChatService";
import NotificationService from "../../../Services/NotificationService";
import SocketService from "../../../Services/SocketService";
import DataParser from "../../../utlits/Dataparser";

const ChatContext = createContext(null);

// ── Helpers ────────────────────────────────────────────────────────────────────
const _getImgUrl = (img) => {
  if (!img || img === "null" || img === "undefined") return null;
  if (img.startsWith("http")) return img;
  const base = (process.env.REACT_APP_BASE_URL || "http://localhost:5026/api/v1").split("/api/v1")[0];
  if (img.includes("/")) {
    const n = img.startsWith("/") ? img : `/${img}`;
    return `${base}${n.startsWith("/public") ? n : `/public${n}`}`;
  }
  return (process.env.REACT_APP_USER_DOCS_PATH || `${base}/public/users/docs/`) + img;
};

const _msgPreview = (data) => {
  if (!data) return "New message";
  if (data.type === "audio") return "🎤 Voice message";
  if (data.attachments?.length)
    return `📎 ${data.attachments[0].originalName || data.attachments[0].name || "Attachment"}`;
  return data.text || data.content || "New message";
};

// ── Hidden-rooms helpers (localStorage, per-user soft delete) ─────────────────
const _hiddenKey = (userId) => `hidden_chat_rooms_${userId}`;

const _getHidden = (userId) => {
  try { return JSON.parse(localStorage.getItem(_hiddenKey(userId)) || "[]"); }
  catch { return []; }
};

const _addHidden = (userId, roomId) => {
  const list = _getHidden(userId);
  if (!list.includes(String(roomId))) {
    localStorage.setItem(_hiddenKey(userId), JSON.stringify([...list, String(roomId)]));
  }
};

const _removeHidden = (userId, roomId) => {
  const list = _getHidden(userId);
  localStorage.setItem(_hiddenKey(userId), JSON.stringify(list.filter(id => id !== String(roomId))));
};

const _filterHidden = (rooms, userId) => {
  const hidden = _getHidden(String(userId));
  return rooms.filter(r => !hidden.includes(String(r._id)));
};

// ── Declined chat invites helpers (localStorage) ─────────────────────────────
const _declinedKey = (userId) => `declined_chat_invites_${userId}`;

const _getDeclined = (userId) => {
  try { return JSON.parse(localStorage.getItem(_declinedKey(userId)) || "[]"); }
  catch { return []; }
};

const _addDeclined = (userId, roomId) => {
  const list = _getDeclined(userId);
  if (!list.includes(String(roomId))) {
    localStorage.setItem(_declinedKey(userId), JSON.stringify([...list, String(roomId)]));
  }
};

const _extractPendingInvitesFromNotifications = (notifications, userId, joinedRoomIds = new Set()) => {
  if (!Array.isArray(notifications)) return [];
  const declined = _getDeclined(String(userId));
  const pendingMap = new Map();

  notifications.forEach(notif => {
    const type = notif.typ;
    const title = String(notif.title || "").toLowerCase();
    const isInvite = (type === "chat" || type === "channel") &&
      (title.includes("invited you") || title.includes("invitation")) &&
      Boolean(notif.identifier);

    if (!isInvite) return;
    const roomId = String(notif.description || "");
    const token = notif.identifier;
    if (!roomId || !token) return;
    if (joinedRoomIds.has(roomId) || declined.includes(roomId)) return;

    const subject = String(notif.email_subject || "");
    const marker = " invited you to ";
    const idx = subject.indexOf(marker);
    let roomName = idx >= 0 ? subject.slice(idx + marker.length) : "";
    if (!roomName && notif.title) {
      const tMarker = "invited you to ";
      const tIdx = notif.title.toLowerCase().indexOf(tMarker);
      if (tIdx >= 0) roomName = notif.title.slice(tIdx + tMarker.length);
    }
    if (roomName.startsWith("#")) roomName = roomName.slice(1).trim();
    if (!roomName) roomName = "Channel";

    const inviterName = [notif.assigned?.first_name, notif.assigned?.last_name].filter(Boolean).join(" ") || "Someone";

    if (!pendingMap.has(roomId)) {
      pendingMap.set(roomId, {
        _id: roomId,
        name: roomName,
        type: "channel",
        parent_id: notif.parent_id || null,
        isPendingInvite: true,
        inviteToken: token,
        inviterName: inviterName,
        unreadCount: 0,
      });
    }
  });

  return Array.from(pendingMap.values());
};

// Stamp _local_cleared_at onto each room from localStorage so ChatModal always has it
const _stampCleared = (rooms, userId) => {
  const uid = String(userId);
  return rooms.map(r => {
    const key = `chat_cleared_at_${uid}_${String(r._id || r.id || "")}`;
    const ts = localStorage.getItem(key);
    return ts ? { ...r, _local_cleared_at: ts } : r;
  });
};

// ── Provider ───────────────────────────────────────────────────────────────────
export const ChatProvider = ({ children }) => {
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const [isNewDmMode, setIsNewDmMode] = useState(false);
  const [isNewChannelMode, setIsNewChannelMode] = useState(false);
  const [isInboxOpen, setIsInboxOpen] = useState(true);
  const [isChannelsOpen, setIsChannelsOpen] = useState(true);
  const [onlineUserIds, setOnlineUserIds] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState({});
  // Channel invite opened from a notification: ChatModal shows the channel screen with
  // Accept / Decline instead of the composer. { token, roomId, inviterName, roomName }
  const [invitePreview, setInvitePreview] = useState(null);
  const [isAcceptingInvite, setIsAcceptingInvite] = useState(false);

  const activeRoomRef   = useRef(null);
  const roomsRef        = useRef([]);
  const isModalOpenRef  = useRef(false);
  const openChatRef     = useRef(null);   // stable ref so socket closure can call latest openChat
  const navigateRef     = useRef(null);   // stable ref so socket closure can call latest navigate

  // ── Notification-click safe-open ─────────────────────────────────────────────
  // If the user is on the login/auth page when a notification is clicked,
  // store the room ID and open it after login + navigation.
  const PENDING_NOTIF_ROOM = "planit_pending_notif_room";
  const _authRoutes = ['/', '/forgot-password', '/initial-reset-password'];
  const _isAuthRoute = (p) => _authRoutes.includes(p) || p.startsWith('/reset-password');

  const safeOpenChat = (room) => {
    if (!room) return;
    const livePath = window.location.pathname;
    const liveId   = DataParser.GetLoggedInUser()?.user?.userdata?.id;
    if (liveId && !_isAuthRoute(livePath)) {
      if (openChatRef.current) openChatRef.current(room);
    } else {
      // Not safe — park room ID; ChatModal will open it after login
      localStorage.setItem(PENDING_NOTIF_ROOM, String(room._id));
    }
  };

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { activeRoomRef.current  = activeRoom;  }, [activeRoom]);
  useEffect(() => { roomsRef.current       = rooms;       }, [rooms]);
  useEffect(() => { isModalOpenRef.current = isModalOpen; }, [isModalOpen]);

  // Close the chat modal whenever the user navigates to a different page
  useEffect(() => {
    if (isModalOpenRef.current) {
      setIsModalOpen(false);
      setInvitePreview(null);
    }
  }, [location.pathname]);

  // ── Service worker registration + notification permission ────────────────────
  const swRegistrationRef = useRef(null);

  useEffect(() => {
    // Register the service worker for reliable notifications (especially macOS)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then(reg => { swRegistrationRef.current = reg; })
        .catch(err => console.warn('[SW] Registration failed — notifications will use new Notification() fallback:', err));
    }

    // Request notification permission on first user click/key (browsers require a gesture).
    // We fire on the VERY NEXT interaction so users get the prompt quickly.
    if ("Notification" in window && Notification.permission === "default") {
      let permRequested = false;
      const requestOnInteraction = () => {
        if (permRequested) return;
        permRequested = true;
        Notification.requestPermission().then(result => {
        }).catch(() => {});
        document.removeEventListener("click", requestOnInteraction, { capture: true, once: true });
        document.removeEventListener("keydown", requestOnInteraction, { capture: true, once: true });
      };
      document.addEventListener("click", requestOnInteraction, { capture: true, once: true });
      document.addEventListener("keydown", requestOnInteraction, { capture: true, once: true });
    }

    // Handle chat notification clicks from the header notification panel
    const handleOpenChatRoom = (e) => {
      const { roomId, room } = e.detail;
      // If a full room object is provided (e.g. from acceptInvite response), use it directly
      // so we don't need the room already in roomsRef (user may not be a member yet).
      if (room && room._id) {
        setRooms(prev => prev.some(r => String(r._id) === String(room._id)) ? prev : [room, ...prev]);
        safeOpenChat(room);
        return;
      }
      const r = roomsRef.current.find(rm => String(rm._id) === String(roomId));
      if (r) { safeOpenChat(r); return; }
      // Not in the cached list yet (e.g. joined just now) — fetch it from the server.
      if (!roomId) return;
      ChatService.getRooms()
        .then(res => {
          const fresh = res?.Good ? (res.rooms || []).find(rm => String(rm._id) === String(roomId)) : null;
          if (!fresh) return;
          setRooms(prev => prev.some(x => String(x._id) === String(fresh._id)) ? prev : [fresh, ...prev]);
          safeOpenChat(fresh);
        })
        .catch(() => {});
    };
    window.addEventListener("openChatRoom", handleOpenChatRoom);

    // Channel-invite notification click: show the channel screen with Accept / Decline.
    // Already a member -> just open the channel.
    const handleInvitePrompt = (e) => {
      const { token, roomId, inviterName, roomName } = e.detail || {};
      if (roomId && roomsRef.current.some(rm => String(rm._id) === String(roomId) && !rm.isPendingInvite)) {
        window.dispatchEvent(new CustomEvent("openChatRoom", { detail: { roomId } }));
        return;
      }
      if (!token) return;
      if (roomId) {
        setRooms(prev => {
          if (prev.some(r => String(r._id) === String(roomId))) return prev;
          return [{
            _id: String(roomId),
            name: (roomName || "Channel").replace(/^#/, "").trim(),
            type: "channel",
            parent_id: null,
            isPendingInvite: true,
            inviteToken: token,
            inviterName: inviterName || "Someone",
            unreadCount: 0,
          }, ...prev];
        });
      }
      setActiveRoom(null);
      setIsNewDmMode(false);
      setIsNewChannelMode(false);
      setInvitePreview(e.detail);
      setIsModalOpen(true);
    };
    window.addEventListener("chatInvitePrompt", handleInvitePrompt);

    // Handle notification clicks posted from the service worker
    const handleSwMessage = (event) => {
      if (event.data?.type !== 'CHAT_NOTIFICATION_CLICK') return;
      const { roomId, url } = event.data;
      const r = roomsRef.current.find(rm => String(rm._id) === String(roomId));
      if (url && navigateRef.current) {
        navigateRef.current(url);
      }
      if (r) {
        setTimeout(() => safeOpenChat(r), 200);
      } else if (roomId) {
        localStorage.setItem(PENDING_NOTIF_ROOM, String(roomId));
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleSwMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSwMessage);
      window.removeEventListener("openChatRoom", handleOpenChatRoom);
      window.removeEventListener("chatInvitePrompt", handleInvitePrompt);
    };
  }, []);

  // ── Socket handlers + room bootstrap ────────────────────────────────────────
  useEffect(() => {
    const currentUser = DataParser.GetLoggedInUser();
    if (!currentUser?.user?.userdata) return;

    const userId = currentUser.user.userdata.id;

    // Connect the socket here (provider always mounts) so the receiver is
    // always connected, not just when they open the chat modal.
    SocketService.connect();
    SocketService.emit("registerUser", String(userId));
    SocketService.emit("getOnlineUsers");

    const syncRoomsAndInvites = () => {
      const companyId = DataParser.CompanyId();
      Promise.allSettled([
        ChatService.getRooms(),
        companyId ? NotificationService.getChatInvites(companyId) : Promise.resolve(null),
      ]).then(([rRes, nRes]) => {
        const serverRooms = (rRes.status === "fulfilled" && rRes.value?.Good) ? rRes.value.rooms : null;
        const visible = serverRooms ? _stampCleared(_filterHidden(serverRooms, String(userId)), String(userId)) : null;

        const notifList = (nRes.status === "fulfilled" && nRes.value?.data?.notifications) ? nRes.value.data.notifications : [];
        const joinedIds = new Set((visible || roomsRef.current.filter(r => !r.isPendingInvite)).map(r => String(r._id)));
        const pendingInvites = _extractPendingInvitesFromNotifications(notifList, String(userId), joinedIds);

        setRooms(prev => {
          const prevMap = new Map(prev.map(r => [String(r._id), r]));
          let baseList = [];

          if (visible) {
            const serverIds = new Set(visible.map(r => String(r._id)));
            const localOnly = prev.filter(r => !r.isPendingInvite && !serverIds.has(String(r._id)));
            const mergedJoined = visible.map(r => {
              const existing = prevMap.get(String(r._id));
              if (!existing) {
                SocketService.emit("joinChatRoom", r._id);
                return r;
              }
              return {
                ...r,
                _local_cleared_at: r._local_cleared_at,
                unreadCount: Math.max(r.unreadCount || 0, existing.unreadCount || 0)
              };
            });
            baseList = [...mergedJoined, ...localOnly];
          } else {
            baseList = prev.filter(r => !r.isPendingInvite);
          }

          const baseIds = new Set(baseList.map(r => String(r._id)));
          const validPending = pendingInvites.filter(p => !baseIds.has(String(p._id)));
          const pendingIds = new Set(validPending.map(p => String(p._id)));
          const existingPending = prev.filter(r => r.isPendingInvite && !baseIds.has(String(r._id)) && !pendingIds.has(String(r._id)) && !_getDeclined(String(userId)).includes(String(r._id)));

          const all = [...baseList, ...validPending, ...existingPending];
          all.forEach(r => {
            if (!r.isPendingInvite) SocketService.emit("joinChatRoom", r._id);
          });
          return all;
        });
      }).catch(() => {});
    };

    const handleConnect = () => {
      SocketService.emit("registerUser", String(userId));
      SocketService.emit("getOnlineUsers");
      roomsRef.current.forEach(r => {
        if (!r.isPendingInvite) SocketService.emit("joinChatRoom", r._id);
      });
      syncRoomsAndInvites();
    };
    SocketService.on("connect", handleConnect);
    syncRoomsAndInvites();

    const handleNotificationEvent = (payload) => {
      if (!payload) return;
      // the server sends { company_id, assigned_to, data: <the notification> }: read the notification itself
      // (reading the wrapper left a new invitation out of the channels until the page was reloaded)
      const data = payload.data && typeof payload.data === "object"
        ? { ...payload.data, assigned_to: payload.assigned_to ?? payload.data.assigned_to }
        : payload;
      const curId = DataParser.UserId();
      if (data.assigned_to && String(data.assigned_to) !== String(curId)) return;

      const type = data.typ;
      const title = String(data.title || "").toLowerCase();
      const isInvite = (type === "chat" || type === "channel") &&
        (title.includes("invited you") || title.includes("invitation")) &&
        Boolean(data.identifier);

      if (isInvite) {
        const roomId = String(data.description || "");
        const token = data.identifier;
        if (!roomId || !token) return;
        if (_getDeclined(String(curId)).includes(roomId)) return;

        const subject = String(data.email_subject || "");
        const marker = " invited you to ";
        const idx = subject.indexOf(marker);
        let roomName = idx >= 0 ? subject.slice(idx + marker.length) : "";
        if (!roomName && data.title) {
          const tMarker = "invited you to ";
          const tIdx = data.title.toLowerCase().indexOf(tMarker);
          if (tIdx >= 0) roomName = data.title.slice(tIdx + tMarker.length);
        }
        if (roomName.startsWith("#")) roomName = roomName.slice(1).trim();
        if (!roomName) roomName = "Channel";

        // the live notification has no inviter details: "<inviter> invited you to <channel>" names them
        const inviterName = [data.assigned?.first_name, data.assigned?.last_name].filter(Boolean).join(" ")
          || (idx > 0 ? subject.slice(0, idx).trim() : "") || "Someone";

        setRooms(prev => {
          if (prev.some(r => String(r._id) === roomId && !r.isPendingInvite)) return prev;
          const filtered = prev.filter(r => String(r._id) !== roomId);
          return [{
            _id: roomId,
            name: roomName,
            type: "channel",
            parent_id: data.parent_id || null,
            isPendingInvite: true,
            inviteToken: token,
            inviterName: inviterName,
            unreadCount: 0,
          }, ...filtered];
        });
      }
    };

    const notificationSound = new Audio(
      "https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3"
    );
    const currentUserId = currentUser.user.userdata.id;

    const handleNewRoom = (room) => {
      if (_getHidden(String(currentUserId)).includes(String(room._id))) return;
      SocketService.emit("joinChatRoom", room._id);
      setRooms(prev => {
        if (prev.find(r => r._id === room._id)) return prev;
        return [room, ...prev];
      });
    };

    // After accepting a channel invite the user gets "memberJoined" on every open tab — add the
    // channel to the sidebar there too (handleNewRoom skips rooms already listed).
    const handleMemberJoined = ({ room } = {}) => {
      if (!room?._id) return;
      const isMember = (room.members || []).some(m => String(m?.id ?? m) === String(currentUserId));
      if (isMember) handleNewRoom(room);
    };

    const handleNewMessage = (data) => {
      // If the room was hidden (deleted from view), un-hide it — new message means conversation resumes
      const hiddenRooms = _getHidden(String(currentUserId));
      const wasHidden = hiddenRooms.includes(String(data.room_id));
      if (wasHidden) {
        _removeHidden(String(currentUserId), String(data.room_id));
        // unhideRoom will be chained with getRooms below so there's no race condition
        // Keep the cleared_at timestamp — Chat.jsx uses it to hide pre-deletion messages
        // Fall through — room will be re-added to sidebar via the roomExists branch below
      }

      const dataRoomId = String(data.room_id);

      // Guard: ignore messages for rooms the current user is not a member of.
      // This prevents stray socket-room memberships from leaking notifications.
      // wasHidden rooms are still ours — they were just locally removed from the sidebar.
      const knownRoom = roomsRef.current.find(r => String(r._id) === dataRoomId);
      if (!knownRoom && !wasHidden) return;

      if (String(data.sender_id) !== String(currentUserId)) {
        const room = knownRoom;
        const isMuted = room?.is_muted;

        // Suppress sound for muted rooms
        if (!isMuted) notificationSound.play().catch(() => {});

        // Always ACK delivery from ChatContext (always mounted, not just when modal is open)
        if (data._id) {
          SocketService.emit("messageDelivered", {
            messageId: data._id,
            senderId: data.sender_id,
            roomId: data.room_id,
          });
        }

        // Only notify when not already looking at this room and not muted
        const isViewing =
          isModalOpenRef.current && String(activeRoomRef.current?._id) === dataRoomId;

        if (!isViewing && !isMuted && "Notification" in window && Notification.permission === "granted") {
          const roomLabel = room?.type === "channel" || room?.type === "project"
            ? `# ${room.name}` : null;

          const title = roomLabel
            ? `${data.sender_name || "New message"} · ${roomLabel}`
            : data.sender_name || "New message";

          const cid = DataParser.CompanyIdentifier();

          // ── Simple, reliable notification ──────────────────────────────────
          // Do NOT include icon — a broken/auth-blocked image URL silently kills
          // the entire notification in Chrome, Firefox and Safari.
          // Use a unique tag so each message gets its own popup (no suppression).
          try {
            const osNotif = new Notification(title, {
              body: _msgPreview(data),
              tag: `chat-${data._id || dataRoomId}-${Date.now()}`,
            });
            osNotif.onclick = () => {
              osNotif.close();
              window.focus();
              const r = roomsRef.current.find(rm => String(rm._id) === dataRoomId);
              if (navigateRef.current && cid) {
                navigateRef.current(`/${cid}/employee-attendance`);
              }
              setTimeout(() => safeOpenChat(r || { _id: dataRoomId }), 500);
            };
            setTimeout(() => osNotif.close(), 8000);
          } catch (e) {
            console.warn('[Notif] new Notification() failed:', e);
          }
        }

        // ── In-app notification → send to header notification panel ──────────
        // Only when not actively viewing that room.
        if (!isViewing) {
          const notifRoom = room;
          const isChannel = notifRoom?.type === "channel" || notifRoom?.type === "project";
          const notifTitle = isChannel
            ? `#${notifRoom?.name || "Channel"}`
            : data.sender_name || "New message";
          const notifBody = isChannel
            ? `${data.sender_name}: ${_msgPreview(data)}`
            : _msgPreview(data);

          // Check if the current user is @mentioned in this message
          const isMention = Array.isArray(data.mentions) &&
            data.mentions.some(m => String(m) === String(currentUserId));

          // Dispatch a custom event so Notofication.jsx can pick it up without coupling
          window.dispatchEvent(new CustomEvent("chatNotification", {
            detail: {
              id: `chat-${dataRoomId}-${Date.now()}`,
              roomId: dataRoomId,
              title: notifBody.length > 60 ? notifBody.slice(0, 60) + "…" : notifBody,
              senderName: data.sender_name || "",
              senderImage: data.sender_image || null,
              createdAt: new Date().toISOString(),
              type: "chat",
              isMention,
            }
          }));
        }
      }

      setRooms(prev => {
        const dataRoomId = String(data.room_id);
        const roomExists = prev.find(r => String(r._id) === dataRoomId);
        if (!roomExists) {
          // Join the socket room immediately so future messages aren't missed
          SocketService.emit("joinChatRoom", data.room_id);
          // New message arrived for a room not in state — could be a previously-hidden room
          // that the backend auto-unhides because a message arrived after my_visible_from.
          // Remove the localStorage hide entry so _filterHidden doesn't suppress it.
          _removeHidden(String(currentUserId), String(data.room_id));
          ChatService.getRooms()
            .then(res => {
              if (res.Good) {
                const visible = _stampCleared(_filterHidden(res.rooms, currentUserId), currentUserId);
                setRooms(prev2 => {
                  const prevMap = new Map(prev2.map(r => [String(r._id), r]));
                  const serverIds = new Set(visible.map(r => String(r._id)));
                  const localOnly = prev2.filter(r => !serverIds.has(String(r._id)));
                  const isViewing = isModalOpenRef.current && String(activeRoomRef.current?._id) === dataRoomId;
                  const merged = visible.map(r => {
                    const existing = prevMap.get(String(r._id));
                    const base = existing
                      ? { ...r, _local_cleared_at: r._local_cleared_at, unreadCount: Math.max(r.unreadCount || 0, existing.unreadCount || 0) }
                      : r;
                    if (String(r._id) === dataRoomId) {
                      return { ...base, last_message: data, unreadCount: isViewing ? 0 : (base.unreadCount || 0) + 1, force_unread: isViewing ? false : base.force_unread };
                    }
                    return base;
                  });
                  merged.forEach(r => SocketService.emit("joinChatRoom", r._id));
                  return [...merged, ...localOnly];
                });
              }
            })
            .catch(() => {});
          return prev;
        }
        return prev.map(r => {
          if (String(r._id) !== dataRoomId) return r;
          const isViewing = isModalOpenRef.current && String(activeRoomRef.current?._id) === dataRoomId;
          // Re-stamp cleared_at: the clear may have happened after the initial rooms load
          // (when _stampCleared runs), so we must check localStorage on every message update.
          const uid = String(currentUserId);
          const rid = String(r._id || r.id || "");
          const clearedAt = r._local_cleared_at
            || (uid && rid ? localStorage.getItem(`chat_cleared_at_${uid}_${rid}`) : null)
            || null;
          return {
            ...r,
            ...(clearedAt ? { _local_cleared_at: clearedAt } : {}),
            last_message: data,
            unreadCount: isViewing ? 0 : (r.unreadCount || 0) + 1,
          };
        });
      });
    };

    const handleProjectUpdate = (data) => {
      // A project created in another company can't add a room for this user: skip reloading the room list
      const myCompanyId = Number(DataParser.CompanyId());
      if (myCompanyId > 0 && data.company_id != null && Number(data.company_id) !== myCompanyId) return;
      if (data.action === "create") {
        setTimeout(() => {
          ChatService.getRooms()
            .then(res => {
              if (res.Good) {
                const visible = _filterHidden(res.rooms, currentUserId);
                setRooms(prev => {
                  const prevMap = new Map(prev.map(r => [String(r._id), r]));
                  const serverIds = new Set(visible.map(r => String(r._id)));
                  const localOnly = prev.filter(r => !serverIds.has(String(r._id)));
                  const merged = visible.map(r => {
                    const existing = prevMap.get(String(r._id));
                    return existing
                      ? { ...r, unreadCount: Math.max(r.unreadCount || 0, existing.unreadCount || 0) }
                      : r;
                  });
                  return [...merged, ...localOnly];
                });
              }
            })
            .catch(() => {});
        }, 2000);
      }
    };

    const handleRoomDeleted = (data) => {
      const deletedId = String(data.roomId || data.room_id);
      setRooms(prev => prev.filter(r => String(r._id) !== deletedId));
      if (String(activeRoomRef.current?._id) === deletedId) {
        setActiveRoom(null);
        setIsModalOpen(false);
      }
    };

    const handleUserLeftRoom = (data) => {
      setRooms(prev => prev.map(r =>
        String(r._id) === String(data.roomId)
          ? { ...r, members: (r.members || []).filter(m => String(m.id || m._id) !== String(data.userId)) }
          : r
      ));
    };

    // Mute / Mark-as-unread / Clear-unread — synced across all tabs for the same user
    const handleRoomSettingUpdated = ({ roomId, type, is_muted, name }) => {
      // a channel renamed (by its owner or an admin): the open channel shows the new name too
      if (type === "rename" && name) {
        setActiveRoom(prev => (prev && String(prev._id) === String(roomId) ? { ...prev, name } : prev));
      }
      setRooms(prev => prev.map(r => {
        if (String(r._id) !== String(roomId)) return r;
        if (type === "rename")       return name ? { ...r, name } : r;
        if (type === "mute")         return { ...r, is_muted };
        if (type === "force_unread") return { ...r, unreadCount: Math.max(r.unreadCount || 0, 1), force_unread: true };
        if (type === "clear_unread") return { ...r, unreadCount: 0, force_unread: false };
        return r;
      }));
    };

    const _parseUid = (data) => {
      if (data === null || data === undefined) return '';
      if (typeof data === 'object' && !Array.isArray(data)) return String(data.userId || data.user_id || data.id || data._id || '');
      return String(data);
    };

    const _parseUids = (data) => {
      if (Array.isArray(data)) return data.map(_parseUid).filter(uid => uid && uid !== 'undefined' && uid !== '');
      const uid = _parseUid(data);
      return (uid && uid !== 'undefined') ? [uid] : [];
    };

    // These events come for every user of the platform. When the set wouldn't change, the same set is kept,
    // so React skips re-rendering the chat and the sidebar for nothing.
    const handleUserOnline = (data) => {
      const uids = _parseUids(data);
      if (uids.length === 0) return;
      setOnlineUserIds(prev => (uids.every(uid => prev.has(uid)) ? prev : new Set([...prev, ...uids])));
    };

    const handleUserOffline = (data) => {
      const uids = _parseUids(data);
      if (uids.length === 0) return;
      setOnlineUserIds(prev => {
        if (!uids.some(uid => prev.has(uid))) return prev;
        const next = new Set(prev);
        uids.forEach(uid => next.delete(uid));
        return next;
      });
    };

    const handleOnlineUsersList = (data) => {
      const uids = _parseUids(data);
      if (uids.length > 0) {
        setOnlineUserIds(prev => (uids.every(uid => prev.has(uid)) ? prev : new Set([...prev, ...uids])));
      }
    };

    const handleTypingContext = (data) => {
      const { room_id, user_id, user_name, isTyping } = data;
      if (String(user_id) === String(userId)) return;
      setTypingUsers(prev => {
        const roomTyping = { ...(prev[room_id] || {}) };
        if (isTyping) roomTyping[user_id] = user_name;
        else delete roomTyping[user_id];
        return { ...prev, [room_id]: roomTyping };
      });
    };

    SocketService.on("notification",           handleNotificationEvent);
    SocketService.on("newRoom",                 handleNewRoom);
    SocketService.on("memberJoined",            handleMemberJoined);
    SocketService.on("receiveChatMessage",      handleNewMessage);
    SocketService.on("project_update",          handleProjectUpdate);
    SocketService.on("roomDeleted",             handleRoomDeleted);
    SocketService.on("userLeftRoom",            handleUserLeftRoom);
    SocketService.on("chatRoomSettingUpdated",  handleRoomSettingUpdated);
    SocketService.on("userOnline",              handleUserOnline);
    SocketService.on("userOffline",             handleUserOffline);
    SocketService.on("onlineUsers",             handleOnlineUsersList);
    SocketService.on("onlineUsersList",         handleOnlineUsersList);
    SocketService.on("userTyping",              handleTypingContext);
    SocketService.on("typing",                  handleTypingContext);

    return () => {
      SocketService.off("connect",                handleConnect);
      SocketService.off("notification",           handleNotificationEvent);
      SocketService.off("newRoom",                handleNewRoom);
      SocketService.off("memberJoined",           handleMemberJoined);
      SocketService.off("receiveChatMessage",     handleNewMessage);
      SocketService.off("project_update",         handleProjectUpdate);
      SocketService.off("roomDeleted",            handleRoomDeleted);
      SocketService.off("userLeftRoom",           handleUserLeftRoom);
      SocketService.off("chatRoomSettingUpdated", handleRoomSettingUpdated);
      SocketService.off("userOnline",             handleUserOnline);
      SocketService.off("userOffline",            handleUserOffline);
      SocketService.off("onlineUsers",            handleOnlineUsersList);
      SocketService.off("onlineUsersList",        handleOnlineUsersList);
      SocketService.off("userTyping",             handleTypingContext);
      SocketService.off("typing",                 handleTypingContext);
    };
  }, []);

  const openChat = (room, rect = null) => {
    if (room?.isPendingInvite) {
      setActiveRoom(null);
      setIsNewDmMode(false);
      setIsNewChannelMode(false);
      setInvitePreview({
        token: room.inviteToken,
        roomId: room._id,
        inviterName: room.inviterName || "Someone",
        roomName: room.name || "Channel",
      });
      setIsModalOpen(true);
      if (rect) setAnchorRect(rect);
      return;
    }
    setInvitePreview(null);
    setActiveRoom(room);
    setIsModalOpen(true);
    setIsNewDmMode(false);
    if (rect) setAnchorRect(rect);
    if (room?._id) {
      setRooms(prev =>
        prev.map(r => r._id === room._id ? { ...r, unreadCount: 0 } : r)
      );
      // Expand the correct sidebar section for this room type
      if (room.type === "channel" || room.type === "project") {
        setIsChannelsOpen(true);
      } else if (room.type === "direct") {
        setIsInboxOpen(true);
      }
    }
  };

  const openNewDm = (rect = null) => {
    setInvitePreview(null);
    setActiveRoom(null);
    setIsModalOpen(true);
    setIsNewDmMode(true);
    setIsNewChannelMode(false);
    if (rect) setAnchorRect(rect);
  };

  const openNewChannel = (rect = null) => {
    setInvitePreview(null);
    setActiveRoom(null);
    setIsModalOpen(true);
    setIsNewChannelMode(true);
    setIsNewDmMode(false);
    if (rect) setAnchorRect(rect);
  };

  // Keep refs current so socket closures always call the latest versions
  openChatRef.current  = openChat;
  navigateRef.current  = navigate;

  const closeChat = () => {
    setIsModalOpen(false);
    setInvitePreview(null);
    setActiveRoom(null);
    setIsNewDmMode(false);
    setIsNewChannelMode(false);
  };

  const refreshRooms = () => {
    const user = DataParser.GetLoggedInUser();
    const userId = user?.user?.userdata?.id;
    const companyId = DataParser.CompanyId();
    if (!userId) return;

    Promise.allSettled([
      ChatService.getRooms(),
      companyId ? NotificationService.getChatInvites(companyId) : Promise.resolve(null),
    ]).then(([rRes, nRes]) => {
      const serverRooms = (rRes.status === "fulfilled" && rRes.value?.Good) ? rRes.value.rooms : null;
      const visible = serverRooms ? _stampCleared(_filterHidden(serverRooms, String(userId)), String(userId)) : null;

      const notifList = (nRes.status === "fulfilled" && nRes.value?.data?.notifications) ? nRes.value.data.notifications : [];
      const joinedIds = new Set((visible || roomsRef.current.filter(r => !r.isPendingInvite)).map(r => String(r._id)));
      const pendingInvites = _extractPendingInvitesFromNotifications(notifList, String(userId), joinedIds);

      setRooms(prev => {
        const prevMap = new Map(prev.map(r => [String(r._id), r]));
        let baseList = [];

        if (visible) {
          const serverIds = new Set(visible.map(r => String(r._id)));
          const localOnly = prev.filter(r => !r.isPendingInvite && !serverIds.has(String(r._id)));
          const mergedJoined = visible.map(r => {
            const existing = prevMap.get(String(r._id));
            if (!existing) {
              SocketService.emit("joinChatRoom", r._id);
              return r;
            }
            return {
              ...r,
              _local_cleared_at: r._local_cleared_at,
              unreadCount: Math.max(r.unreadCount || 0, existing.unreadCount || 0)
            };
          });
          baseList = [...mergedJoined, ...localOnly];
        } else {
          baseList = prev.filter(r => !r.isPendingInvite);
        }

        const baseIds = new Set(baseList.map(r => String(r._id)));
        const validPending = pendingInvites.filter(p => !baseIds.has(String(p._id)));
        const pendingIds = new Set(validPending.map(p => String(p._id)));
        const existingPending = prev.filter(r => r.isPendingInvite && !baseIds.has(String(r._id)) && !pendingIds.has(String(r._id)) && !_getDeclined(String(userId)).includes(String(r._id)));

        const all = [...baseList, ...validPending, ...existingPending];
        all.forEach(r => {
          if (!r.isPendingInvite) SocketService.emit("joinChatRoom", r._id);
        });
        return all;
      });
    }).catch(() => {});
  };

  // Accept: join the channel, add it to the sidebar and switch the preview to the real channel
  // screen ("openChatRoom" with the room adds it to rooms and opens it, clearing the preview).
  const acceptInvitePreview = () => {
    if (!invitePreview || isAcceptingInvite) return;
    const { token, roomId } = invitePreview;
    setIsAcceptingInvite(true);
    ChatService.acceptInvite(token)
      .then(res => {
        if (!res?.Good || !res?.room) throw new Error(res?.message);
        SocketService.emit("joinChatRoom", res.room._id);
        setRooms(prev => {
          const filtered = prev.filter(r => String(r._id) !== String(res.room._id));
          return [res.room, ...filtered];
        });
        window.dispatchEvent(new CustomEvent("openChatRoom", { detail: { room: res.room } }));
        message.success(`You've joined "${res.room.name}"!`);
        setTimeout(() => refreshRooms(), 1500);
      })
      .catch(err => {
        const msg = err?.response?.data?.message || err?.message || "Invalid or expired invitation";
        ChatService.getRooms()
          .then(r => {
            const room = r?.Good ? (r.rooms || []).find(rm => String(rm._id) === String(roomId)) : null;
            if (room) {
              setRooms(prev => {
                const filtered = prev.filter(r => String(r._id) !== String(room._id));
                return [room, ...filtered];
              });
              window.dispatchEvent(new CustomEvent("openChatRoom", { detail: { room } }));
            } else {
              message.error(msg);
              closeChat();
            }
          })
          .catch(() => { message.error(msg); closeChat(); });
      })
      .finally(() => setIsAcceptingInvite(false));
  };

  // Decline: remove pending channel from rooms and persist declined ID so it doesn't reappear
  const declineInvitePreview = () => {
    if (isAcceptingInvite) return;
    if (invitePreview?.roomId) {
      const userId = DataParser.GetLoggedInUser()?.user?.userdata?.id;
      if (userId) {
        _addDeclined(String(userId), String(invitePreview.roomId));
      }
      setRooms(prev => prev.filter(r => String(r._id) !== String(invitePreview.roomId)));
    }
    closeChat();
  };

  const hideConversation = (roomId) => {
    const userId = DataParser.GetLoggedInUser()?.user?.userdata?.id;
    if (userId) {
      _addHidden(String(userId), String(roomId));
      // Store deletion timestamp as fallback when backend my_visible_from is not yet propagated
      localStorage.setItem(`chat_cleared_at_${userId}_${roomId}`, new Date().toISOString());
    }
    // Persist server-side: hideRoom marks the room hidden for this user so getRooms won't
    // return it after re-login or on a different device. clearMessages sets my_visible_from
    // so old messages stay hidden if/when the room reappears (e.g. other user messages again).
    // Neither call emits a socket event, so the other participant is not affected.
    ChatService.hideRoom(roomId).catch(() => {});
    ChatService.clearMessages({ roomId }).catch(() => {});
    setRooms(prev => prev.filter(r => String(r._id) !== String(roomId)));
    if (String(activeRoomRef.current?._id) === String(roomId)) {
      setActiveRoom(null);
      setIsModalOpen(false);
    }
  };

  const unhideConversation = (roomId) => {
    const userId = DataParser.GetLoggedInUser()?.user?.userdata?.id;
    if (userId) _removeHidden(String(userId), String(roomId));
  };

  return (
    <ChatContext.Provider value={{
      rooms, setRooms,
      activeRoom, setActiveRoom,
      isModalOpen, anchorRect, setAnchorRect,
      isNewDmMode, setIsNewDmMode,
      isNewChannelMode, setIsNewChannelMode,
      isInboxOpen, setIsInboxOpen,
      isChannelsOpen, setIsChannelsOpen,
      openChat, closeChat, openNewDm, openNewChannel,
      refreshRooms,
      hideConversation,
      unhideConversation,
      onlineUserIds,
      typingUsers,
      invitePreview, isAcceptingInvite, acceptInvitePreview, declineInvitePreview,
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => useContext(ChatContext);
export default ChatContext;
